import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { fetchRoleContext } from '../../services/roleContext'
import { tournamentApi } from '../../services/api'

/**
 * AdminShell — layout wrapper for the super-admin section (/admin/*).
 *
 * Client-side guard: calls /api/auth/role-context once on mount and only
 * renders children if role === 'super_admin'. Otherwise redirects.
 *
 * Backend guards every endpoint anyway via requireGroup(1), so this is
 * UX (no flicker, clean nav) rather than security.
 */
export default function AdminShell({ children, title }) {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [roleChecked, setRoleChecked] = useState(false)
  const [authorized, setAuthorized] = useState(false)
  const [contextErr, setContextErr] = useState(null)
  const [refundCount, setRefundCount] = useState(0)

  // Refund-queue action badge — how many refunds still need InteGolf's action.
  // Refetches on navigation AND when the queue page fires 'integolf:refunds-updated'
  // (so releasing/working a refund clears the badge without a page change).
  useEffect(() => {
    if (!authorized) return
    const refresh = () => tournamentApi.get('/admin/resellers/refund-queue/summary')
      .then(r => setRefundCount(Number(r.data.actionable) || 0)).catch(() => {})
    refresh()
    window.addEventListener('integolf:refunds-updated', refresh)
    return () => window.removeEventListener('integolf:refunds-updated', refresh)
  }, [authorized, location.pathname])

  useEffect(() => {
    let alive = true
    // Prefer the role baked into the cached user (now returned by PlayStay-API
    // /auth/login + /auth/me). No network call required, no proxy dependency.
    // Falls back to fetchRoleContext only for sessions whose backend pre-dates
    // this change (cached user with no `role` field).
    if (user?.role) {
      if (user.role === 'super_admin') setAuthorized(true)
      else { setAuthorized(false); setContextErr(user.role) }
      setRoleChecked(true)
      return
    }
    fetchRoleContext()
      .then(ctx => {
        if (!alive) return
        if (ctx?.role === 'super_admin') setAuthorized(true)
        else { setAuthorized(false); setContextErr(ctx?.role || 'unknown') }
        setRoleChecked(true)
      })
      .catch(err => {
        if (!alive) return
        setRoleChecked(true)
        if (err?.response?.status === 401) navigate('/login')
        else setContextErr(err?.message || 'Could not verify your role')
      })
    return () => { alive = false }
  }, [navigate, user?.role])

  if (!user) return <Navigate to="/login" replace />
  if (!roleChecked) {
    return <CenteredMessage>Verifying access…</CenteredMessage>
  }
  if (!authorized) {
    return (
      <CenteredMessage>
        <p style={{ color: '#dc2626' }}>You do not have access to the admin portal.</p>
        <p style={{ color: '#64748b', fontSize: 13 }}>{contextErr ? `Detected role: ${contextErr}` : null}</p>
        <Link to="/portal" style={{ color: 'var(--brand-600)' }}>Go to portal</Link>
      </CenteredMessage>
    )
  }

  const isActive = (p) => location.pathname === p || location.pathname.startsWith(p + '/')

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex' }}>
      {/* Sidebar */}
      <aside style={{
        width: 240, background: '#0f172a', color: '#cbd5e1',
        display: 'flex', flexDirection: 'column', padding: '24px 0',
        position: 'sticky', top: 0, height: '100vh', overflowY: 'auto',
        flexShrink: 0,
      }}>
        <div style={{ padding: '0 20px 24px', borderBottom: '1px solid #1e293b' }}>
          <Link to="/admin" style={{ color: '#fff', fontWeight: 700, fontSize: 18, textDecoration: 'none' }}>
            InteGolf Admin
          </Link>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, letterSpacing: 1 }}>SUPER ADMIN</div>
        </div>
        <nav style={{ flex: 1, padding: '16px 0' }}>
          <NavItem to="/admin"                      label="Dashboard"        active={isActive('/admin') && location.pathname === '/admin'} />
          <SectionLabel>Resellers</SectionLabel>
          <NavItem to="/admin/reseller-accounts"    label="Accounts"         active={isActive('/admin/reseller-accounts')} />
          <NavItem to="/admin/reseller-invites"     label="Invites"          active={isActive('/admin/reseller-invites')} />
          <NavItem to="/admin/reseller-sales"       label="Sales"            active={isActive('/admin/reseller-sales')} />
          <NavItem to="/admin/refund-queue"         label="Refund Queue"     active={isActive('/admin/refund-queue')} badge={refundCount} />
          <NavItem to="/admin/course-payments"      label="Course Payments"  active={isActive('/admin/course-payments')} />
          <NavItem to="/admin/split-quotes"         label="Split Payments"   active={isActive('/admin/split-quotes')} />
          <NavItem to="/admin/hotel-authorizations" label="Hotel Auth"       active={isActive('/admin/hotel-authorizations')} />
          <SectionLabel>Platform</SectionLabel>
          <NavItem to="/admin/platform-stats"      label="Platform Stats"   active={isActive('/admin/platform-stats')} />
          <NavItem to="/admin/margin-inspector"    label="Margin Inspector" active={isActive('/admin/margin-inspector')} />
          <NavItem to="/admin/course-integrations" label="Course Integrations" active={isActive('/admin/course-integrations')} />
          <NavItem to="/admin/hotel-integrations"  label="Hotel Integrations"  active={isActive('/admin/hotel-integrations')} />
          <NavItem to="/admin/reseller-courses"    label="Reseller Courses"    active={isActive('/admin/reseller-courses')} />
          <NavItem to="/admin/data-migration?tab=testing" label="Integrations" active={isActive('/admin/data-migration') && new URLSearchParams(location.search).get('tab') === 'testing'} />
          <NavItem to="/admin/fee-settings"         label="Fee Settings"     active={isActive('/admin/fee-settings')} />
          <NavItem to="/admin/terms"                label="Terms of Use"     active={isActive('/admin/terms')} />
          <NavItem to="/admin/reseller-payouts"     label="Reseller Payouts" active={isActive('/admin/reseller-payouts')} />
          <NavItem to="/admin/payouts"              label="Payout Batches"   active={isActive('/admin/payouts')} />
          <NavItem to="/admin/data-migration"       label="Data Migration"   active={isActive('/admin/data-migration') && new URLSearchParams(location.search).get('tab') !== 'testing'} />
        </nav>
        <div style={{ padding: '16px 20px', borderTop: '1px solid #1e293b' }}>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>Signed in as</div>
          <div style={{ fontSize: 13, color: '#e2e8f0', wordBreak: 'break-all' }}>{user.email}</div>
          <button
            onClick={() => { logout(); navigate('/login') }}
            style={{
              marginTop: 12, width: '100%', padding: '8px',
              background: 'transparent', color: '#94a3b8',
              border: '1px solid #334155', borderRadius: 6,
              fontSize: 12, cursor: 'pointer',
            }}
          >Sign out</button>
        </div>
      </aside>
      {/* Main */}
      <main style={{ flex: 1, padding: '32px 40px', overflowX: 'auto' }}>
        {title && <h1 style={{ margin: '0 0 24px', fontSize: 26, color: '#0f172a' }}>{title}</h1>}
        {children}
      </main>
    </div>
  )
}

function NavItem({ to, label, active, badge }) {
  return (
    <Link
      to={to}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 20px',
        color: active ? '#fff' : '#cbd5e1',
        background: active ? '#1e293b' : 'transparent',
        textDecoration: 'none',
        fontSize: 14,
        fontWeight: active ? 600 : 400,
        borderLeft: active ? '3px solid #00d19f' : '3px solid transparent',
      }}
    >
      <span style={{ flex: 1 }}>{label}</span>
      {badge > 0 && (
        <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: '#dc2626', borderRadius: 999, minWidth: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px' }}>{badge}</span>
      )}
    </Link>
  )
}

function SectionLabel({ children }) {
  return (
    <div style={{
      padding: '16px 20px 6px', fontSize: 11, color: '#475569',
      letterSpacing: 1.5, fontWeight: 700,
    }}>{children}</div>
  )
}

function CenteredMessage({ children }) {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#f8fafc', flexDirection: 'column', gap: 12, padding: 20, textAlign: 'center',
    }}>
      {children}
    </div>
  )
}
